import clsx from "clsx";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useNavigation } from "../../hooks";
import { useAuth } from "../../hooks/useAuth";
import { useChat } from "../../hooks/useChat";
import { useUser } from "../../hooks/useUser";
import { TERMS_URLS } from "../../utils";
import { Button } from "../catalyst/button";
import { Divider } from "../catalyst/divider";
import { LoadingSpinner } from "../common";
import Icon from "../ui/Icon";

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { isLoggedIn, logout } = useAuth();
  const navigate = useNavigate();
  const { navigateToSettings, navigateToAuth } = useNavigation();
  const { chats, isChatListLoading } = useChat();
  const { userName } = useUser();

  const menuItems = [
    {
      id: "settings",
      label: "설정",
      icon: "settings",
      onClick: () => {
        navigateToSettings();
        onClose();
      },
    },
  ];

  const legalMenuItems = [
    {
      id: "service-terms",
      label: "서비스이용약관",
      icon: "document",
      onClick: () => {
        window.open(TERMS_URLS.SERVICE_TERMS, "_blank", "noopener,noreferrer");
      },
    },
    {
      id: "privacy-policy",
      label: "개인정보처리방침",
      icon: "shield",
      onClick: () => {
        window.open(TERMS_URLS.PRIVACY_POLICY, "_blank", "noopener,noreferrer");
      },
    },
  ];

  const logoutMenuItem = {
    id: "logout",
    label: "로그아웃",
    icon: "log-out",
    onClick: () => {
      logout();
      onClose();
    },
  };

  const loginMenuItem = {
    id: "login",
    label: "로그인/회원가입",
    icon: "user",
    onClick: () => {
      navigateToAuth();
      onClose();
    },
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString("ko-KR", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else if (diffDays === 1) {
      return "어제";
    } else if (diffDays < 7) {
      return `${diffDays}일 전`;
    } else {
      return date.toLocaleDateString("ko-KR", {
        month: "short",
        day: "numeric",
      });
    }
  };

  const handleChatSelect = (chatId: string) => {
    navigate(`/chat/${chatId}`);
    onClose();
  };

  const handleNewChat = () => {
    navigate("/");
    onClose();
  };

  return (
    <>
      {/* 오버레이 */}
      {isOpen && (
        <div
          className="absolute inset-0 bg-bg-dimmed z-40"
          onClick={onClose}
        />
      )}

      {/* 사이드바 */}
      <div
        className={clsx(
          "absolute top-0 left-0 h-full w-80 bg-white shadow-l transform transition-transform duration-300 ease-in-out z-50 flex flex-col",
          isOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {/* 헤더 */}
        <div className="flex items-center justify-between p-4 border-b border-border-default">
          <h2 className="text-l font-semibold text-text-primary">AI홈닥터</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <Icon name="x" className="text-text-secondary" />
          </button>
        </div>

        {/* 채팅 섹션 - 로그인한 사용자만 표시 */}
        {isLoggedIn && (
          <div className="flex-1 flex flex-col min-h-0 border-b border-border-default">
            {/* 새 채팅 버튼 */}
            <div className="p-4 border-b border-border-default">
              <Button
                onClick={handleNewChat}
                color="blue"
                className="w-full"
              >
                <Icon name="plus" size="sm" />
                <span className="font-medium">새 채팅</span>
              </Button>
            </div>

            {/* 채팅 목록 */}
            <div className="flex-1 overflow-y-auto bg-bg-background">
              <div className="p-3">
                <h3 className="text-s font-medium text-text-secondary mb-3">
                  최근 채팅
                </h3>

                {isChatListLoading ? (
                  <div className="flex justify-center py-8">
                    <LoadingSpinner />
                  </div>
                ) : chats.length === 0 ? (
                  <div className="text-center py-8 text-text-tertiary">
                    <Icon
                      name="message-circle"
                      size="lg"
                      className="mx-auto mb-2 text-gray-300"
                    />
                    <p className="text-s">아직 채팅이 없습니다</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {chats.map((chat) => (
                      <div
                        key={chat.id}
                        onClick={() => handleChatSelect(chat.id)}
                        className="group bg-white hover:bg-gray-50 rounded-s p-3 cursor-pointer transition-colors border border-transparent hover:border-border-default"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-text-primary truncate text-s">
                              {chat.title || "새 채팅"}
                            </h4>
                            <p className="text-xs text-text-tertiary mt-1">
                              {formatDate(chat.lastMessageAt)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 비로그인 상태 안내 */}
        {!isLoggedIn && (
          <div className="flex-1 flex flex-col items-center justify-center px-6 py-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                <Icon name="user" size="lg" className="text-blue-500" />
              </div>
              <h3 className="text-l font-semibold text-text-primary mb-2">
                로그인하고 시작하세요
              </h3>
            </div>
          </div>
        )}

        {/* 메뉴 섹션 */}
        <div className="flex flex-col">
          <Divider className="my-0" />
          {/* 통합 메뉴 리스트 */}
          <div className="space-y-1 p-3">
            {/* 로그인한 사용자에게만 설정 메뉴 표시 */}
            {isLoggedIn &&
              menuItems.map((item) => (
                <button
                  key={item.id}
                  onClick={item.onClick}
                  className="w-full flex items-center px-3 py-2.5 hover:bg-gray-50 rounded-s transition-colors group"
                >
                  <Icon
                    name={item.icon}
                    className="text-text-tertiary mr-3 group-hover:text-text-secondary"
                    size="sm"
                  />
                  <span className="text-text-secondary text-s font-medium group-hover:text-text-primary">
                    {item.label}
                  </span>
                </button>
              ))}

            {/* 약관 메뉴 - 모든 사용자에게 표시 */}
            {legalMenuItems.map((item) => (
              <button
                key={item.id}
                onClick={item.onClick}
                className="w-full flex items-center px-3 py-2.5 hover:bg-gray-50 rounded-s transition-colors group"
              >
                <Icon
                  name={item.icon}
                  className="text-text-tertiary mr-3 group-hover:text-text-secondary"
                  size="sm"
                />
                <span
                  className={clsx(
                    "text-text-secondary text-s group-hover:text-text-primary",
                    item.id === "privacy-policy" ? "font-bold" : "font-medium"
                  )}
                >
                  {item.label}
                </span>
              </button>
            ))}

            {/* 로그인/로그아웃 메뉴 */}
            {isLoggedIn ? (
              <button
                key={logoutMenuItem.id}
                onClick={logoutMenuItem.onClick}
                className="w-full flex items-center px-3 py-2.5 hover:bg-gray-50 rounded-s transition-colors group"
              >
                <Icon
                  name={logoutMenuItem.icon}
                  className="text-text-tertiary mr-3 group-hover:text-text-secondary"
                  size="sm"
                />
                <span className="text-text-secondary text-s font-medium group-hover:text-text-primary">
                  {logoutMenuItem.label}
                </span>
              </button>
            ) : (
              <Button
                key={loginMenuItem.id}
                onClick={loginMenuItem.onClick}
                color="blue"
                className="w-full"
              >
                <Icon
                  name={loginMenuItem.icon}
                  className="text-white"
                  size="sm"
                />
                <span className="text-white text-s font-medium">
                  {loginMenuItem.label}
                </span>
              </Button>
            )}
            {/* 사용자 정보 표시 - 로그인한 사용자만 */}
            {isLoggedIn && userName && (
              <div className="w-full flex items-center px-3 py-2.5 rounded-s mb-1">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-3">
                  <Icon name="user" size="sm" className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-s font-medium text-text-primary truncate">
                    {userName}님
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
